import { useState, useEffect, useMemo } from 'react'
import { X, Ellipsis, Trash2 } from '@/components/icons'
import { useForm, Controller } from 'react-hook-form'
import { Button } from './ui/button'
import { FieldError } from './FieldError'
import { PrntrCard, PrntrGroup, PrntrInput } from './ui/prntr-input'
import {
  BareNumInput,
  DateObjectRow,
  PickerRowField,
  SelectRowField,
  TextareaField,
} from './ui/prntr-fields'
import { RepeatPatternSelect, RepeatPattern } from './RepeatPatternSelect'
import { Avatar, AvatarFallback } from './ui/avatar'
import { CrewMemberPickerDrawer } from './CrewMemberPickerDrawer'
import { AircraftPickerDrawer } from './AircraftPickerDrawer'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu'
import { toast } from 'sonner'
import { track, EVENTS } from '@/utils/analytics'
import dayjs from 'dayjs'
import {
  useRosterCreate,
  useRosterDeleteByRange,
  useRosterCheckExistingInRange,
} from '../lib/hooks/roster'
import { useCrewGetAll } from '../lib/hooks/crew'
import { useAircraftGetAll } from '../lib/hooks'
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember'
import { DutyStatus } from '@@api/shared'
import { crewRoleLabels } from '@@api/shared'
import { getRosterStatusBadge } from '../utils/statusColors'
import { getInitials, getPatternLabel } from '../lib/utils'

interface FormData {
  crewId: string
  aircraftId: string
  dutyStatus: DutyStatus
  startDate: Date | null
  endDate: Date | null
  selectedPattern: RepeatPattern | null
  isCustomPattern: boolean
  customDaysOn: number
  customDaysOff: number
  note: string
}

export interface RosterEntrySuccessInfo {
  crewId: string
  startDate: Date
  endDate: Date
}

interface RosterEntryDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultCrewId?: string | null
  defaultAircraftId?: string | null
  defaultStartDate?: Date | null
  defaultEndDate?: Date | null
  defaultDutyStatus?: DutyStatus | null
  defaultPatternDaysOn?: number | null
  defaultPatternDaysOff?: number | null
  onSuccess?: (info: RosterEntrySuccessInfo) => void
  onRemove?: () => void
  quickStartMode?: boolean
}

const statusOptions: DutyStatus[] = [
  DutyStatus.OnDuty,
  DutyStatus.OffDuty,
  DutyStatus.Standby,
  DutyStatus.Training,
  DutyStatus.Leave,
  DutyStatus.Sick,
  DutyStatus.Positioning,
]

const dutyStatusLabels: Record<DutyStatus, string> = {
  [DutyStatus.OnDuty]: 'On Duty',
  [DutyStatus.OffDuty]: 'Off Duty',
  [DutyStatus.Standby]: 'Standby',
  [DutyStatus.Training]: 'Training',
  [DutyStatus.Leave]: 'Leave',
  [DutyStatus.Sick]: 'Sick',
  [DutyStatus.Positioning]: 'Positioning',
}

export function RosterEntryDrawer({
  open,
  onOpenChange,
  defaultCrewId,
  defaultAircraftId,
  defaultStartDate,
  defaultEndDate,
  defaultDutyStatus,
  defaultPatternDaysOn,
  defaultPatternDaysOff,
  onSuccess,
  onRemove,
  quickStartMode = false,
}: RosterEntryDrawerProps) {
  const [showCrewPicker, setShowCrewPicker] = useState(false)
  const [showAircraftPicker, setShowAircraftPicker] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [confirmingOverwrite, setConfirmingOverwrite] = useState(false)

  const { data: crewMembers = [] } = useCrewGetAll()
  const { data: aircraftList = [] } = useAircraftGetAll()
  const { data: currentUser } = useCurrentCrewMember()
  const createMutation = useRosterCreate()
  const deleteByRangeMutation = useRosterDeleteByRange()

  // Determine if we have default pattern (coming from RosterStatusDrawer)
  const hasDefaultPattern = defaultPatternDaysOn != null && defaultPatternDaysOff != null

  // Build initial pattern from defaults
  const initialPattern: RepeatPattern | null = hasDefaultPattern
    ? { unit: 'day' as const, on: defaultPatternDaysOn!, off: defaultPatternDaysOff! }
    : null

  const {
    control,
    watch,
    reset,
    setValue,
    formState: {},
  } = useForm<FormData>({
    defaultValues: {
      crewId: defaultCrewId ?? '',
      aircraftId: defaultAircraftId ?? '',
      dutyStatus: quickStartMode ? DutyStatus.OnDuty : (defaultDutyStatus ?? DutyStatus.OnDuty),
      startDate: defaultStartDate ?? null,
      endDate: defaultEndDate ?? null,
      selectedPattern: initialPattern,
      isCustomPattern: false,
      customDaysOn: defaultPatternDaysOn ?? 7,
      customDaysOff: defaultPatternDaysOff ?? 7,
      note: '',
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (open) {
      const hasDefPattern = defaultPatternDaysOn != null && defaultPatternDaysOff != null
      const defPattern: RepeatPattern | null = hasDefPattern
        ? { unit: 'day' as const, on: defaultPatternDaysOn!, off: defaultPatternDaysOff! }
        : null

      reset({
        crewId: defaultCrewId ?? '',
        aircraftId: defaultAircraftId ?? '',
        dutyStatus: quickStartMode ? DutyStatus.OnDuty : (defaultDutyStatus ?? DutyStatus.OnDuty),
        startDate: defaultStartDate ?? null,
        endDate: defaultEndDate ?? null,
        selectedPattern: defPattern,
        isCustomPattern: false,
        customDaysOn: defaultPatternDaysOn ?? 7,
        customDaysOff: defaultPatternDaysOff ?? 7,
        note: '',
      })
    }
  }, [open, defaultCrewId, defaultAircraftId, defaultStartDate, defaultEndDate, defaultDutyStatus, defaultPatternDaysOn, defaultPatternDaysOff, quickStartMode, reset])

  const watchedCrewId = watch('crewId')
  const watchedAircraftId = watch('aircraftId')

  // Default aircraft (one per company) to pre-select for new entries,
  // used only when no explicit defaultAircraftId was passed in.
  const companyDefaultAircraftId = aircraftList.find((a) => a.isDefault)?.id

  useEffect(() => {
    if (open && companyDefaultAircraftId && !watchedAircraftId) {
      setValue('aircraftId', companyDefaultAircraftId)
    }
  }, [open, companyDefaultAircraftId, watchedAircraftId, setValue])

  // Load admin user's saved custom pattern as defaults when custom is selected
  const adminCustomDaysOn = currentUser?.customPatternDaysOn ?? 7
  const adminCustomDaysOff = currentUser?.customPatternDaysOff ?? 7

  const watchedDutyStatus = watch('dutyStatus')
  const watchedStartDate = watch('startDate')
  const watchedEndDate = watch('endDate')
  const watchedSelectedPattern = watch('selectedPattern')
  const watchedIsCustomPattern = watch('isCustomPattern')
  const watchedCustomDaysOn = watch('customDaysOn')
  const watchedCustomDaysOff = watch('customDaysOff')
  const watchedNote = watch('note')

  // Check for existing roster entries that the create would overwrite.
  // Auto-fetches whenever crew + both dates are set (gcTime: 0).
  const { data: existingInRange } = useRosterCheckExistingInRange(
    watchedCrewId || null,
    watchedStartDate ? dayjs(watchedStartDate).format('YYYY-MM-DD') : null,
    watchedEndDate ? dayjs(watchedEndDate).format('YYYY-MM-DD') : null,
  )

  const calculatedPattern = useMemo(() => {
    if (watchedIsCustomPattern) {
      return {
        daysOn: watchedCustomDaysOn,
        daysOff: watchedCustomDaysOff,
      }
    }

    if (!watchedSelectedPattern) return undefined

    return {
      daysOn: watchedSelectedPattern.on,
      daysOff: watchedSelectedPattern.off,
    }
  }, [
    watchedSelectedPattern,
    watchedIsCustomPattern,
    watchedCustomDaysOn,
    watchedCustomDaysOff,
  ])

  const dateValidationError = useMemo(() => {
    if (!watchedStartDate || !watchedEndDate) return null
    if (dayjs(watchedEndDate).isBefore(dayjs(watchedStartDate), 'day')) {
      return 'End date must be on or after start date.'
    }
    return null
  }, [watchedStartDate, watchedEndDate])

  const isFormValid = useMemo(() => {
    const hasCrewId = !!watchedCrewId
    const hasAircraftId = !!watchedAircraftId
    const hasStartDate = !!watchedStartDate
    const hasEndDate = !!watchedEndDate
    const noDateError = !dateValidationError
    const hasValidCustomPattern =
      !watchedIsCustomPattern ||
      (watchedCustomDaysOn >= 1 && watchedCustomDaysOff >= 1)

    return (
      hasCrewId &&
      hasAircraftId &&
      hasStartDate &&
      hasEndDate &&
      noDateError &&
      hasValidCustomPattern
    )
  }, [
    watchedCrewId,
    watchedAircraftId,
    watchedStartDate,
    watchedEndDate,
    dateValidationError,
    watchedIsCustomPattern,
    watchedCustomDaysOn,
    watchedCustomDaysOff,
  ])

  const resetForm = () => {
    reset({
      crewId: defaultCrewId ?? '',
      aircraftId: defaultAircraftId ?? '',
      dutyStatus: quickStartMode ? DutyStatus.OnDuty : (defaultDutyStatus ?? DutyStatus.OnDuty),
      startDate: defaultStartDate ?? null,
      endDate: defaultEndDate ?? null,
      selectedPattern: initialPattern,
      isCustomPattern: false,
      customDaysOn: defaultPatternDaysOn ?? 7,
      customDaysOff: defaultPatternDaysOff ?? 7,
      note: '',
    })
  }

  const handleClose = () => {
    resetForm()
    setSubmitError(null)
    setConfirmingOverwrite(false)
    onOpenChange(false)
  }

  const overlappingAircraft = existingInRange?.overlappingAircraft ?? []
  const hasOverlap = (existingInRange?.totalEntries ?? 0) > 0 && overlappingAircraft.length > 0

  // Save button: if the create would overwrite existing entries, show the
  // confirm step first; otherwise create immediately.
  const handleSave = () => {
    setSubmitError(null)
    if (hasOverlap) {
      setConfirmingOverwrite(true)
      return
    }
    performCreate()
  }

  const performCreate = async () => {
    setSubmitError(null)

    try {
      await createMutation.mutateAsync({
        crewId: watchedCrewId,
        aircraftId: watchedAircraftId,
        dutyStatus: watchedDutyStatus,
        startDate: dayjs(watchedStartDate).format('YYYY-MM-DD'),
        endDate: dayjs(watchedEndDate).format('YYYY-MM-DD'),
        pattern: calculatedPattern,
        note: watchedNote || undefined,
        customPattern: watchedIsCustomPattern ? { daysOn: watchedCustomDaysOn, daysOff: watchedCustomDaysOff } : undefined,
      })
      const crewName = crewMembers.find(c => c.id === watchedCrewId)?.name ?? 'Crew'
      const startStr = dayjs(watchedStartDate).format('MMM D')
      const endStr = dayjs(watchedEndDate).format('MMM D')
      const statusLabel = dutyStatusLabels[watchedDutyStatus]
      track(EVENTS.ROSTER_ENTRY_CREATED, { dutyStatus: watchedDutyStatus })
      toast.success(`${crewName} added as ${statusLabel} (${startStr} - ${endStr})`)

      onSuccess?.({
        crewId: watchedCrewId,
        startDate: watchedStartDate!,
        endDate: watchedEndDate!,
      })
      handleClose()
    } catch (error: any) {
      console.error('Error saving roster entry:', error)
      setSubmitError(error?.message || 'Failed to add roster entries')
    }
  }

  const handleRemove = async () => {
    if (!defaultCrewId || !defaultStartDate || !defaultEndDate) return

    try {
      await deleteByRangeMutation.mutateAsync({
        crewId: defaultCrewId,
        startDate: dayjs(defaultStartDate).format('YYYY-MM-DD'),
        endDate: dayjs(defaultEndDate).format('YYYY-MM-DD'),
      })
      const crewName = crewMembers.find(c => c.id === defaultCrewId)?.name ?? 'Crew'
      toast.success(`Schedule removed for ${crewName}`)
      onRemove?.()
      handleClose()
    } catch (error: any) {
      console.error('Error removing roster entries:', error)
      toast.error(error?.message || 'Failed to remove roster entries')
    }
  }

  // Editing the assignment after reaching the confirm step returns to the form
  useEffect(() => {
    setConfirmingOverwrite(false)
  }, [watchedCrewId, watchedAircraftId, watchedStartDate, watchedEndDate])

  // When duty status changes to non-OnDuty, clear repeat pattern
  useEffect(() => {
    if (watchedDutyStatus !== DutyStatus.OnDuty) {
      setValue('selectedPattern', null)
      setValue('isCustomPattern', false)
    }
  }, [watchedDutyStatus, setValue])

  // When a repeat pattern is selected, force duty status to OnDuty
  useEffect(() => {
    if ((watchedSelectedPattern || watchedIsCustomPattern) && watchedDutyStatus !== DutyStatus.OnDuty) {
      setValue('dutyStatus', DutyStatus.OnDuty)
    }
  }, [watchedSelectedPattern, watchedIsCustomPattern, watchedDutyStatus, setValue])

  const hasPattern = !!watchedSelectedPattern || watchedIsCustomPattern

  return (
    <>
      <CrewMemberPickerDrawer
        isOpen={showCrewPicker}
        onClose={() => setShowCrewPicker(false)}
        onSelect={(crewId) => {
          setValue('crewId', crewId, { shouldDirty: true })
          setShowCrewPicker(false)
        }}
        selectedCrewId={watchedCrewId || undefined}
        schedulableOnly
      />

      <AircraftPickerDrawer
        isOpen={showAircraftPicker}
        onClose={() => setShowAircraftPicker(false)}
        onSelect={(aircraftId) => {
          setValue('aircraftId', aircraftId, { shouldDirty: true })
          setShowAircraftPicker(false)
        }}
        selectedAircraftId={watchedAircraftId || undefined}
        allowMaintenance
      />

      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-surface-L0 rounded-tl-2xl rounded-tr-2xl shadow-lg max-h-[85dvh] flex flex-col">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="font-semibold text-lg text-content-primary-bold">
              Schedule
            </DrawerTitle>
            <div className="flex items-center gap-3">
              <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DrawerHeader>

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-4">
              {/* Info Message */}
              <div className="bg-background-notice-subtle rounded-lg p-3">
                <p className="text-sm text-background-notice-bold leading-normal tracking-[0.07px]">
                  This will overwrite any entries for{' '}
                  {crewMembers.find(c => c.id === watchedCrewId)?.name ?? 'this crew member'}.
                </p>
              </div>

              {/* Crew card stays when the drawer is scoped to one member */}
              {(() => {
                const selectedCrew = crewMembers.find(c => c.id === watchedCrewId)
                if (!defaultCrewId || !selectedCrew) return null
                const initials = getInitials(selectedCrew.firstName, selectedCrew.lastName)
                const displayName =
                  selectedCrew.firstName && selectedCrew.lastName
                    ? `${selectedCrew.firstName} ${selectedCrew.lastName}`
                    : selectedCrew.name
                const roleLabel = crewRoleLabels[selectedCrew.crewRole] ?? selectedCrew.crewRole
                const badge = getRosterStatusBadge(watchedDutyStatus)
                return (
                  <div className="bg-surface-L0 border border-border-L1 rounded-2xl px-4 py-2">
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Avatar className="size-8 shrink-0"><AvatarFallback className="text-xs text-background font-medium" style={selectedCrew.avatarColor ? { backgroundColor: selectedCrew.avatarColor } : undefined}>{initials}</AvatarFallback></Avatar>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm text-content-primary-bold leading-normal tracking-[0.07px]">
                            {displayName}
                          </span>
                          <span className="text-xs text-content-primary-subtle leading-normal tracking-[0.18px]">
                            {roleLabel}
                          </span>
                        </div>
                      </div>
                      {badge && (
                        <span className={`inline-flex items-center px-2 py-[3px] rounded-full text-xs font-medium leading-normal tracking-[0.18px] ${badge.className}`}>
                          {badge.text}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })()}

              <PrntrCard>
                {!defaultCrewId && (
                  <PickerRowField
                    control={control}
                    name="crewId"
                    label="Crew"
                    iconLeft={(() => {
                      const selectedCrew = crewMembers.find(c => c.id === watchedCrewId)
                      if (!selectedCrew) return undefined
                      return (
                        <Avatar className="size-5 shrink-0"><AvatarFallback className="text-[10px] font-medium text-background" style={selectedCrew.avatarColor ? { backgroundColor: selectedCrew.avatarColor } : undefined}>{getInitials(selectedCrew.firstName, selectedCrew.lastName)}</AvatarFallback></Avatar>
                      )
                    })()}
                    display={(value) => {
                      const selectedCrew = crewMembers.find(c => c.id === value)
                      return selectedCrew
                        ? selectedCrew.firstName && selectedCrew.lastName
                          ? `${selectedCrew.firstName} ${selectedCrew.lastName}`
                          : selectedCrew.name
                        : null
                    }}
                    onOpen={() => setShowCrewPicker(true)}
                  />
                )}
                <PickerRowField
                  control={control}
                  name="aircraftId"
                  label="Aircraft"
                  display={(value) => {
                    const selectedAircraft = aircraftList.find(a => a.id === value)
                    return selectedAircraft
                      ? `${selectedAircraft.registration}${selectedAircraft.type ? ` · ${selectedAircraft.type}` : ''}`
                      : null
                  }}
                  onOpen={() => setShowAircraftPicker(true)}
                />
                <DateObjectRow control={control} name="startDate" label="Start Date" />
                <DateObjectRow
                  control={control}
                  name="endDate"
                  label="End Date"
                  minDate={watchedStartDate ? dayjs(watchedStartDate).startOf('day').toDate() : undefined}
                  error={dateValidationError ?? undefined}
                />
              </PrntrCard>

              {/* Repeat Pattern */}
              <div className="flex flex-col gap-2">
                <label className="font-medium text-sm text-content-primary-bold">
                  Repeat Pattern
                </label>
                <RepeatPatternSelect
                  selectedPattern={watchedSelectedPattern}
                  isCustomPattern={watchedIsCustomPattern}
                  customDaysOn={currentUser?.customPatternDaysOn}
                  customDaysOff={currentUser?.customPatternDaysOff}
                  disabled={watchedDutyStatus !== DutyStatus.OnDuty}
                  onSelect={(pattern) => {
                    if (pattern === 'custom') {
                      setValue('isCustomPattern', true, { shouldDirty: true })
                      setValue('selectedPattern', null, { shouldDirty: true })
                      setValue('customDaysOn', adminCustomDaysOn, { shouldDirty: true })
                      setValue('customDaysOff', adminCustomDaysOff, { shouldDirty: true })
                    } else {
                      setValue('isCustomPattern', false, { shouldDirty: true })
                      setValue('selectedPattern', pattern, { shouldDirty: true })
                    }
                  }}
                />
              </div>

              {/* Custom Pattern Inputs */}
              {watchedIsCustomPattern && (
                <>
                  <PrntrGroup>
                    <Controller
                      name="customDaysOn"
                      control={control}
                      render={({ field }) => (
                        <PrntrInput label="Days On" empty={field.value === 0}>
                          <BareNumInput
                            value={field.value === 0 ? null : field.value}
                            onChange={(next) => field.onChange(next ?? 0)}
                            onBlur={field.onBlur}
                            integer
                          />
                        </PrntrInput>
                      )}
                    />
                    <Controller
                      name="customDaysOff"
                      control={control}
                      render={({ field }) => (
                        <PrntrInput label="Days Off" empty={field.value === 0}>
                          <BareNumInput
                            value={field.value === 0 ? null : field.value}
                            onChange={(next) => field.onChange(next ?? 0)}
                            onBlur={field.onBlur}
                            integer
                          />
                        </PrntrInput>
                      )}
                    />
                  </PrntrGroup>
                  {(watchedCustomDaysOn < 1 || watchedCustomDaysOff < 1) && (
                    <FieldError message="Days On and Days Off must be at least 1." />
                  )}
                </>
              )}

              {/* Duty Status */}
              {!quickStartMode && (
                <PrntrGroup>
                  <SelectRowField
                    control={control}
                    name="dutyStatus"
                    label="Duty Status"
                    options={statusOptions.map((status) => ({
                      value: status,
                      label: dutyStatusLabels[status],
                    }))}
                    placeholder="Select duty status"
                    readOnly={hasPattern}
                  />
                </PrntrGroup>
              )}

              <TextareaField
                control={control}
                name="note"
                label="Note (optional)"
                placeholder="Add a note for this period..."
              />
            </div>
          </div>

          {/* Footer */}
          <DrawerFooter>
            <FieldError message={submitError ?? undefined} />
            {confirmingOverwrite ? (
              <>
                <div className="bg-background-notice-subtle rounded-lg p-3 space-y-1">
                  {overlappingAircraft.map((ac) => (
                    <p
                      key={ac.aircraftId}
                      className="text-sm text-background-notice-bold leading-normal tracking-[0.07px]"
                    >
                      This will overwrite roster entries for {ac.registration} from {ac.startDate} – {ac.endDate}.
                    </p>
                  ))}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="secondary"
                    size="mini"
                    onClick={() => setConfirmingOverwrite(false)}
                    disabled={createMutation.isPending}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    size="mini"
                    onClick={performCreate}
                    disabled={createMutation.isPending || !isFormValid}
                    className="flex-1"
                  >
                    {createMutation.isPending ? 'Saving...' : 'Overwrite'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex gap-2 pt-2">
                <Button
                  variant="secondary"
                  size="mini"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="mini"
                  onClick={handleSave}
                  disabled={createMutation.isPending || !isFormValid}
                  className="flex-1"
                >
                  {createMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}
